import { Injectable, inject } from '@angular/core';
import type {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
} from '@angular/common/http';
import type { Observable} from 'rxjs';
import { NEVER, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BasicDialogComponent } from '../shared/components/basic-dialog/basic-dialog.component';
import { ConfirmationDialogComponent, type ConfirmationDialogData } from '../shared/components/confirmation-dialog/confirmation-dialog.component';
import { LoaderService } from '../core/services/loader.service';
import config from '../../config';
import { fetchAuthSession } from 'aws-amplify/auth';
import type { SimpleDialog } from '../shared/models/simple-dialog';
import * as pakoLib from 'pako';

// Feature flag: Enable/disable request body compression
const ENABLE_COMPRESSION = true; // Compression enabled for requests > 5KB

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private dialog = inject(MatDialog);
  private loaderService = inject(LoaderService);
  router = inject(Router);


  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const originalUrl = req.url;
    const simpleDialog: SimpleDialog = {
      uiTitle: 'Your session has expired',
      uiMessage:
        "To continue, please click 'OK' to be redirected to the login page.",
      redirect: `${config.APP_DOMAIN_ENDPOINT}/auth/login`,
    };

    // Skip token processing for non-API requests
    if (!originalUrl.includes(`${config.API_GATEWAY_ENV_ENDPOINT}`)) {
      return next.handle(req);
    }

    // Convert Promise to Observable and use switchMap to wait for completion
    return from(
      fetchAuthSession().catch(() => {
        // Catch errors from fetchAuthSession specifically
        // Return empty session to proceed without auth
        return { tokens: undefined };
      })
    ).pipe(
      switchMap((session) => {
        const accessToken = session.tokens?.accessToken?.toString();
        const idToken = session.tokens?.idToken;
        const tokenExpirationTime = idToken?.payload.exp;
        const now = Math.floor(Date.now() / 1000);

        // Check for token expiration
        if (tokenExpirationTime && tokenExpirationTime <= now) {
          // Token expired, showing dialog
          this.loaderService.hide();
          this.dialog.open(BasicDialogComponent, { data: simpleDialog });
          return NEVER;
        }

        // Add the token to the request
        if (accessToken) {
          let headers = req.headers.set('Authorization', accessToken);
          const body = req.body;
          let modifiedReq = req;

          // Calculate request body size and compress if > 5KB
          if (ENABLE_COMPRESSION && body && typeof body === 'object' && !(body instanceof Blob) && !(body instanceof ArrayBuffer)) {
            const bodyString = JSON.stringify(body);
            const bodySize = new Blob([bodyString]).size;

            // Compress request body if > 5KB (5120 bytes)
            if (bodySize > 5120) {
              try {
                // Compress the body using gzip
                const compressed = pakoLib.gzip(bodyString);

                // Convert to base64 string for transmission
                const base64Compressed = btoa(String.fromCharCode(...Array.from(compressed)));

                // Check if compressed body is still > 8KB (8192 bytes)
                const compressedSize = new Blob([base64Compressed]).size;
                if (compressedSize > 8192) {
                  const payloadErrorDialog: ConfirmationDialogData = {
                    title: 'Request Too Large',
                    message: 'The request payload is too large to process. Please reduce the amount of data and try again.',
                    confirmText: 'OK',
                    hideCancel: true,
                    type: 'warning',
                    icon: 'error_outline'
                  };
                  this.loaderService.hide();
                  this.dialog.open(ConfirmationDialogComponent, {
                    data: payloadErrorDialog,
                    disableClose: true,
                    width: '450px'
                  });
                  return NEVER;
                }

                // Set Content-Type to application/gzip so Lambda can identify and decompress
                headers = headers.set('Content-Type', 'application/gzip');

                modifiedReq = req.clone({
                  headers,
                  body: base64Compressed,
                  responseType: 'json' as const
                });

                return next.handle(modifiedReq);
              } catch {
                // Fall through to send uncompressed
              }
            }
          }

          // Send uncompressed request (body ≤ 5KB or compression failed)
          // Note: Accept-Encoding is automatically controlled by the browser
          modifiedReq = req.clone({ headers, body });
          return next.handle(modifiedReq);
        } else {
          // No access token - proceed without auth (let backend return 401/403)
          return next.handle(req);
        }
      })
    );
  }
}
